<template>
  <!-- Her tip and the club's answer to it, or the door — and after an answer,
       BOTH. The receipt is what happened; the door is what she can do next, and
       an answered tip is history rather than a reason to stop her naming a
       second shop. Only an OPEN tip shuts the door. -->
  <div v-if="receipt || showDoor || showChangeLink" class="product-suggestion-door">
    <p v-if="receipt" class="product-suggestion-door__receipt">{{ receipt }}</p>

    <button
      v-if="showDoor"
      type="button"
      class="product-suggestion-door__btn"
      @click.stop="openSheet"
    >
      {{ t('item.suggestSupplier.productCta') }}
    </button>

    <!-- Correcting an OPEN tip, same as the order-line sheet's «غيّر اقتراحك»
         (tips-wave P3-A, U-79/W-C). `showDoor` is false while a tip is open —
         she has already told us — so without this the door simply vanished
         and an open feed suggestion could never be edited. Its OWN class,
         deliberately not `__btn`: the two never render together, but sharing
         a selector would make it impossible to assert "the door is gone"
         without also asserting something about this link. -->
    <button
      v-if="showChangeLink"
      type="button"
      class="product-suggestion-door__change"
      @click.stop="openSheet"
    >
      {{ t('item.suggestSupplier.pending.change') }}
    </button>
  </div>
</template>

<script setup>
/**
 * ProductSuggestionDoor — «تعرف مين عنده؟» beside the «مش متوفر» badge.
 *
 * BE→FE brief `2026-09-09-product-level-suggestion-and-oos-feed.md`.
 *
 * ── WHO THIS IS FOR ─────────────────────────────────────────────────────────
 * The suggestion door has always lived on `order-box-items`, which means only
 * someone who ALREADY ORDERED the thing could open it. That refused the one
 * person the badge is actually for: the member reading «مش متوفر» on a post
 * before she orders, who may well know exactly where to get it. This is that
 * door, and it posts to the product.
 *
 * ── WHY IT DOES NOT HOST THE SHEET ──────────────────────────────────────────
 * It renders inside a post card, which renders inside a `transform`ed row of a
 * virtualized list — and a transformed ancestor is the containing block for
 * `position: fixed`, so a sheet mounted here would be trapped inside that row.
 * It calls `useProductSuggestion().open()` instead, and the page's single
 * `<product-suggestion-host>` renders the sheet outside those rows. See the
 * composable for the whole reasoning.
 *
 * ── TWO RESOURCES, ONE SENTENCE ─────────────────────────────────────────────
 * `product` is what she is suggesting FOR and what the POST goes to. `source`
 * is where the receipt is read FROM, and they differ on a post card: a post
 * carries the union of its products' tips, newest wins, because a card has room
 * for one receipt and a post carries more than one product about 1% of the time.
 * On the product page the two are the same object.
 *
 * The label writer is the one shared with her order line, so a member who names
 * a shop from a post and then opens her order reads the same sentence about the
 * same tip in both places.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProductSuggestion } from '@/composables/commerce/useProductSuggestion.js';
import { useCurrency } from '@/composables/useCurrency.js';
import {
  hasOpenSupplierSuggestion,
  readSupplierSuggestion,
  suggestionLabel,
} from '@/utils/supplierSuggestion.js';

defineOptions({ name: 'ProductSuggestionDoor' });

const props = defineProps({
  // The product the tip is about. The door does not render without one — a
  // non-product post has nothing to name a shop for.
  product: {
    type: Object,
    default: null,
  },
  // Where to read her existing tip from. Defaults to the product; a post card
  // passes the POST, whose `mySuggestion` is the union of its products'.
  source: {
    type: Object,
    default: null,
  },
  // The post she tapped from, so the receipt can paint on that card straight
  // away rather than waiting for the next feed read.
  postId: {
    type: [String, Number],
    default: null,
  },
});

const { t, locale } = useI18n({ useScope: 'global' });
// Only for the receipt's price arm — a tip she gave with a price says it back.
const { formatCurrency } = useCurrency();
const { open } = useProductSuggestion();

const receiptSource = computed(() => props.source ?? props.product);

const suggestion = computed(() => readSupplierSuggestion(receiptSource.value, 'mySuggestion'));

const receipt = computed(() =>
  suggestionLabel(suggestion.value, { t, locale: locale.value, formatMoney: formatCurrency }),
);

// NOT gated on the product being out of stock — the API is not, deliberately,
// so a mark cleared while her sheet is open cannot turn her tap into a 403. The
// APP decides where to show it, and the host renders this beside the badge.
const hasOpenTip = computed(
  () => hasOpenSupplierSuggestion(receiptSource.value, 'mySuggestion'),
);
const showDoor = computed(() => props.product?.id != null && !hasOpenTip.value);
// The mirror image of `showDoor`: while her tip is OPEN, offer to correct it
// instead of the "name a shop" CTA (tips-wave P3-A, U-79/W-C).
const showChangeLink = computed(() => props.product?.id != null && hasOpenTip.value);

function openSheet() {
  open(props.product, props.postId);
}
</script>

<style scoped>
/* Sits under the availability badge inside the card's facts strip, quieter than
   it: the badge is the news, this is the offer. Logical properties throughout,
   so RTL needs no override block. */
.product-suggestion-door {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin: 4px 12px 0;
}

.product-suggestion-door__receipt {
  margin: 0;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.5;
  color: var(--poolit-text-secondary);
}

/* A link, not a button-shaped control: the card already carries the buying
   controls below, and a second filled button beside them would compete with
   the action she came for. Shared with the "change what I sent" link — same
   weight, same reasoning. */
.product-suggestion-door__btn,
.product-suggestion-door__change {
  padding: 0;
  border: 0;
  background: none;
  color: var(--poolit-primary-500);
  font-family: inherit;
  font-size: var(--poolit-font-size-xs);
  font-weight: var(--poolit-font-weight-medium);
  line-height: 1.5;
  text-align: start;
  text-decoration: underline;
  cursor: pointer;
}

.product-suggestion-door__btn:disabled,
.product-suggestion-door__change:disabled {
  opacity: 0.6;
}
</style>
